import { getCookie } from 'helpers/VanillaCookie';

/**
 * Helper class to store and read from the local storage
 * using different user identifiers for mapping
 * (account, profile, or cookie value)
 * Defaults to profile if user is signed in
 * otherwise use the cookie user identifier
 * The helper allows storing string, number, boolean, or objects
 * The class implements store(key), get(key), and remove(key) methods
 * and also implements identifier switch methods
 */
class UserStorage {
    #accountId;
    #activeProfileId;
    #cookieUserId;
    #storageId;

    /**
     * @returns {UserStorage}
     */
    constructor() {
        this.#accountId = CBS?.Registry?.user?.id;
        this.#activeProfileId = CBS?.Registry?.user?.profile_id;
        this.#cookieUserId = getCookie('ovvuid');
        this.#storageId = this.#activeProfileId ?? this.#cookieUserId;
        return this;
    }

    /**
     * Switch storageId to main userId
     * @returns {UserStorage}
     */
    useAccountId() {
        this.#storageId = this.#accountId ?? this.#cookieUserId;
        return this;
    }

    /**
     * Switch storageId to cookie user id
     * @returns {UserStorage}
     */
    useCookieId() {
        this.#storageId = this.#cookieUserId;
        return this;
    }

    /**
     * Switch storageId to active profile id
     * @returns {UserStorage}
     */
    useProfileId() {
        this.#storageId = this.#activeProfileId ?? this.#cookieUserId;
        return this;
    }

    getStorageId() {
        return this.#storageId;
    }

    /**
     *
     * @param key
     * @param data
     * @returns {boolean}
     */
    store(key, data) {
        if (!this.#storageId) {
            return false;
        }
        const dataType = typeof data;
        let dataToStore = null;
        switch (dataType) {
            case 'object':
                dataToStore = JSON.stringify(data);
                break;
            case 'string':
                dataToStore = data;
                break;
            case 'boolean':
                dataToStore = data.toString();
                break;
            case 'number':
                dataToStore = data.toString();
                break;
            default:
                return false;
        }
        try {
            window.localStorage.setItem(`${key}-${this.#storageId}`, dataToStore);
            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     *
     * @param key
     * @returns {undefined|any|boolean|Number|String}
     */
    get(key) {
        if (!this.#storageId) {
            return undefined;
        }
        const data = window.localStorage.getItem(`${key}-${this.#storageId}`);
        return data ? this.#parseData(data) : undefined;
    }

    /**
     *
     * @param key
     * @returns {boolean}
     */
    remove(key) {
        if (!this.#storageId) {
            return false;
        }
        try {
            window.localStorage.removeItem(`${key}-${this.#storageId}`);
            return true;
        } catch (e) {
            return false;
        }
    }

    #hasJsonStructure(str) {
        try {
            const result = JSON.parse(str);
            const type = Object.prototype.toString.call(result);
            return type === '[object Object]';
        } catch (err) {
            return false;
        }
    }

    #parseData(data) {
        if (this.#hasJsonStructure(data)) {
            return JSON.parse(data);
        } else if (data === 'true' || data === 'false') {
            return data === 'true';
        } else if (isNaN(data)) {
            return data;
        } else {
            return Number(data);
        }
    }
}

export { UserStorage };
